import type { Aggregate, OpEnvelope } from './ops.ts'
import type { HlcClock } from './hlc.ts'
import type { IdSource } from './boundaries.ts'
import type {
  EntrySource,
  KindValue,
  Owner,
  OutcomeValue,
  PhaseValue,
  Residence,
  StageValue,
  StatusValue,
  TripResidence,
} from './state.ts'
import type { TagString } from './tags.ts'

/**
 * The **strict** half of the reader (`docs/sync-protocol.md` §5.3). Its
 * counterpart is `payloads.ts`, which is tolerant: foerier is strict about
 * what it emits and liberal in what it accepts. This is the only place an op
 * payload is ever constructed, so the app cannot author a malformed one.
 *
 * Every builder returns an {@link OpSpec} — the `type`-specific part of an op
 * — and {@link authorOp} completes it into an {@link OpEnvelope} by stamping
 * the envelope fields the app never gets to choose per-op: `id` and `hlc` are
 * minted fresh every time, `household_id` and `device_id` come from the
 * session, not the call site.
 */

/** The `type`-specific half of an op, before {@link authorOp} completes it. */
export interface OpSpec {
  aggregate: Aggregate
  aggregate_id: string
  type: string
  payload: Record<string, unknown>
}

/**
 * The session an op is authored under: fixed for as long as a device stays
 * signed in, and never something an individual builder call supplies.
 */
export interface OpAuthor {
  household_id: string
  device_id: string
  ids: IdSource
  hlc: HlcClock
}

/**
 * Completes an {@link OpSpec} into an {@link OpEnvelope}. `id` is stamped
 * from the `IdSource` and `hlc` from the `HlcClock`, in that order, and
 * neither is ever regenerated once stamped — the builder never mints either
 * itself.
 */
export function authorOp(author: OpAuthor, spec: OpSpec): OpEnvelope {
  return {
    id: author.ids.next(),
    household_id: author.household_id,
    aggregate: spec.aggregate,
    aggregate_id: spec.aggregate_id,
    type: spec.type,
    hlc: author.hlc.issue(),
    device_id: author.device_id,
    payload: spec.payload,
  }
}

/**
 * The outbound mirror of `payloads.ts`'s `readOwner`:
 * `{ type: 'person', personId }` → `{ type: 'person', person_id: personId }`.
 * `{ type: 'shared' }` needs no translation.
 */
function wireOwner(owner: Owner): Record<string, unknown> {
  if (owner.type === 'shared') return { type: 'shared' }
  return { type: 'person', person_id: owner.personId }
}

/** `sync-protocol.md` §4.1: creates the Place; seeds `name`. */
export function placeRecorded(id: string, name: string): OpSpec {
  return {
    aggregate: 'place',
    aggregate_id: id,
    type: 'place.recorded',
    payload: { name },
  }
}

/** `sync-protocol.md` §4.1: sets `name`. */
export function placeRenamed(id: string, name: string): OpSpec {
  return {
    aggregate: 'place',
    aggregate_id: id,
    type: 'place.renamed',
    payload: { name },
  }
}

/**
 * `sync-protocol.md` §4.1: sets the tombstone. Payload is `{}` — present and
 * empty, never absent, never `null` (§1.1).
 */
export function placeRemoved(id: string): OpSpec {
  return {
    aggregate: 'place',
    aggregate_id: id,
    type: 'place.removed',
    payload: {},
  }
}

/**
 * `sync-protocol.md` §4.3: creates the Gear and seeds each **present** field
 * as its own register. `residence`, `owner` and `owned_count` are optional —
 * an absent field is omitted from the payload, never written as `undefined`
 * or `null` (§1.3).
 */
export function gearRecorded(
  id: string,
  fields: {
    name: string
    container: boolean
    kind: KindValue
    residence?: Residence
    owner?: Owner
    owned_count?: number
  },
): OpSpec {
  return {
    aggregate: 'gear',
    aggregate_id: id,
    type: 'gear.recorded',
    payload: {
      name: fields.name,
      container: fields.container,
      kind: fields.kind,
      ...(fields.residence === undefined
        ? {}
        : { residence: fields.residence }),
      ...(fields.owner === undefined ? {} : { owner: wireOwner(fields.owner) }),
      ...(fields.owned_count === undefined
        ? {}
        : { owned_count: fields.owned_count }),
    },
  }
}

/** `sync-protocol.md` §4.3: sets `name`. */
export function gearRenamed(id: string, name: string): OpSpec {
  return {
    aggregate: 'gear',
    aggregate_id: id,
    type: 'gear.renamed',
    payload: { name },
  }
}

/** `sync-protocol.md` §4.3: sets the **home** residence. A trip never touches it. */
export function gearRehomed(id: string, residence: Residence): OpSpec {
  return {
    aggregate: 'gear',
    aggregate_id: id,
    type: 'gear.rehomed',
    payload: { residence },
  }
}

/** `sync-protocol.md` §4.3: sets `kind`. One register, one value (invariant 5). */
export function gearKindSet(id: string, kind: KindValue): OpSpec {
  return {
    aggregate: 'gear',
    aggregate_id: id,
    type: 'gear.kind_set',
    payload: { kind },
  }
}

/**
 * `sync-protocol.md` §4.3: sets `owned_count`, absolutely — never a delta.
 * The payload field is `count`, not `owned_count`; the catalogue names it
 * that way to keep the op self-describing without repeating the register it
 * writes.
 */
export function gearOwnedCountSet(id: string, count: number): OpSpec {
  return {
    aggregate: 'gear',
    aggregate_id: id,
    type: 'gear.owned_count_set',
    payload: { count },
  }
}

/**
 * `sync-protocol.md` §4.3: sets `owner`.
 *
 * The cheapest op in the catalogue after the tag pair — {@link wireOwner}
 * already existed, because `gear.recorded` may carry `owner?` and S2 wired the
 * camelCase-to-wire mapping for it.
 */
export function gearOwnershipSet(id: string, owner: Owner): OpSpec {
  return {
    aggregate: 'gear',
    aggregate_id: id,
    type: 'gear.ownership_set',
    payload: { owner: wireOwner(owner) },
  }
}

/**
 * `sync-protocol.md` §4.3: sets the per-tag register to **present** (§3.4).
 *
 * The parameter is a {@link TagString}, not a `string`, and that is the
 * design's spelling defence made structural: there is no Tag entity and no
 * rename op, so the picker is the only place a spelling is ever decided, and
 * `normalizeTag` is the only way to make one. The `#` every screen draws is
 * never stored.
 */
export function gearTagApplied(id: string, tag: TagString): OpSpec {
  return {
    aggregate: 'gear',
    aggregate_id: id,
    type: 'gear.tag_applied',
    payload: { tag },
  }
}

/**
 * `sync-protocol.md` §4.3: sets the per-tag register to **absent** (§3.4).
 *
 * Not a delete — one register, written `false`, carrying a clock like any
 * other write. That is what lets a concurrent re-apply win on merit rather
 * than on which device happened to sync first, and what makes an apply/remove
 * race resolve by plain LWW instead of by arrival order.
 */
export function gearTagRemoved(id: string, tag: TagString): OpSpec {
  return {
    aggregate: 'gear',
    aggregate_id: id,
    type: 'gear.tag_removed',
    payload: { tag },
  }
}

/**
 * `sync-protocol.md` §4.3: sets the tombstone. Payload is `{}` — present and
 * empty, never absent, never `null` (§1.1).
 */
export function gearRetired(id: string): OpSpec {
  return {
    aggregate: 'gear',
    aggregate_id: id,
    type: 'gear.retired',
    payload: {},
  }
}

/**
 * `sync-protocol.md` §4.3: clears the tombstone if strictly later. Payload is
 * `{}` — present and empty, never absent, never `null` (§1.1).
 */
export function gearRestored(id: string): OpSpec {
  return {
    aggregate: 'gear',
    aggregate_id: id,
    type: 'gear.restored',
    payload: {},
  }
}

/** `sync-protocol.md` §4.2: creates the Person; seeds `name`. */
export function personRecorded(id: string, name: string): OpSpec {
  return {
    aggregate: 'person',
    aggregate_id: id,
    type: 'person.recorded',
    payload: { name },
  }
}

/**
 * `sync-protocol.md` §4.2: sets `name`.
 *
 * **`string | null`, settled by this slice.** §4.2 typed the row `{name}` and
 * said in as many words that the slice which folds it settles the question.
 * `PersonState.name` is `Register<string | null>`, so an explicit `null` is a
 * clear like any other write and an absent field leaves the register alone
 * (§1.3). No carve-out: this is the rule the six other name registers already
 * follow.
 *
 * {@link personRecorded} above keeps its `string` parameter. Its only callers
 * — the join screen and the People screen's `+ NEW PERSON` — have a name in
 * hand, and a Person recorded with no name is not a state any screen can
 * author.
 */
export function personRenamed(id: string, name: string | null): OpSpec {
  return {
    aggregate: 'person',
    aggregate_id: id,
    type: 'person.renamed',
    payload: { name },
  }
}

/**
 * `sync-protocol.md` §4.4: creates the Trip; seeds `name`.
 *
 * It does **not** seed the phase — `trip.created`'s payload is
 * `{name, from_trip_id?}` and carries no phase field at all, so the reducer
 * writes `draft` itself on this op's own clock (spec §1.3). Three properties
 * fall out of that and none of them needs a special case: a
 * `trip.phase_moved` delivered first wins on its own strictly-later stamp, a
 * re-delivered creation is idempotent, and no client can author a Trip that
 * arrives already `closed`.
 *
 * `from_trip_id` is **S14's**, and it is the one parameter here that may be
 * left off. The reducer has folded the field into `TripState.fromTripId`
 * since S6 — it had to, because §5.4 froze this payload the moment S6
 * shipped — while `authoring.ts` deliberately had no parameter for it,
 * since a builder parameter with no caller is a shape frozen on a guess.
 * S14 is the caller: the template copy is the only thing that authors one.
 *
 * **The key is omitted, never sent as `null`.** `TripState.fromTripId` is
 * `Register<string>` and not nullable, so a `null` would author an
 * instruction no reader in this codebase honours (§1.3) — and no op detaches
 * a Trip from its source, so there is nothing a clear could mean.
 *
 * `name` is a `string`, though {@link tripRenamed} and the reader both accept
 * `null`: no screen can author a Trip with no name — F3 step 1 requires
 * one — and the nullable type exists for a *reader* meeting an op some other
 * build emitted (spec §1.2). {@link personRecorded} draws the same line for
 * the same reason.
 */
export function tripCreated(
  id: string,
  name: string,
  fromTripId?: string,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: id,
    type: 'trip.created',
    payload: {
      name,
      ...(fromTripId !== undefined && { from_trip_id: fromTripId }),
    },
  }
}

/**
 * `sync-protocol.md` §4.4: sets `name`.
 *
 * **`string | null`, settled by this slice** — the seventh and eighth `name`
 * rows, closed exactly as S4 closed the sixth (§4.2's `person.renamed`) and
 * from the same general rule rather than a new one: `TripState.name` is
 * `Register<string | null>`, so an explicit `null` clears and an absent field
 * leaves the register alone (§1.3).
 */
export function tripRenamed(id: string, name: string | null): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: id,
    type: 'trip.renamed',
    payload: { name },
  }
}

/**
 * `sync-protocol.md` §4.4: emits `start` and/or `end`, which write the
 * `startDate` and `endDate` registers — two independent registers, each
 * following the absent-versus-null rule separately (§1.3).
 *
 * A key the caller omits is **omitted from the payload entirely**
 * ({@link gearRecorded}'s spread idiom), never emitted as `undefined` or
 * coerced to `null`: omitting leaves that date alone, and `null` clears it.
 * The screen therefore emits only what changed, which is what stops one
 * device's date edit from reverting the other's.
 *
 * The two names differ on purpose — the same split `gear.owned_count_set`
 * already has over an `ownedCount` register (spec §1.4) — and are named at
 * both ends here so nobody "fixes" one to match the other. A date is emitted
 * as written and read back verbatim; there is no `YYYY-MM-DD` gate on either
 * side.
 */
export function tripDatesSet(
  id: string,
  dates: { start?: string | null; end?: string | null },
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: id,
    type: 'trip.dates_set',
    payload: {
      ...(dates.start === undefined ? {} : { start: dates.start }),
      ...(dates.end === undefined ? {} : { end: dates.end }),
    },
  }
}

/**
 * `sync-protocol.md` §4.4: moves the phase. One register, one value, so
 * exclusivity is structural and there is nothing to guard — and invariant 16
 * makes every move expressible in **either** direction, so this builder
 * encodes no transition graph. Entering `closed` is unguarded until S10 has
 * something that could be open; leaving it is confirmed on the screen, never
 * in the op.
 */
export function tripPhaseMoved(id: string, phase: PhaseValue): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: id,
    type: 'trip.phase_moved',
    payload: { phase },
  }
}

/**
 * `sync-protocol.md` §4.4: sets the Trip's tombstone. The payload is empty —
 * there is nothing to say beyond the fact — and the confirmation is the
 * screen's (invariant 15), never the op's and never the reducer's.
 *
 * **There is no partner, and that absence is the feature.**
 * {@link gearRetired} / {@link gearRestored} are an ordinary LWW pair over
 * one register (§3.5); the catalogue defines one Trip tombstone op and no
 * restore, which is what S14's confirm states to a human in as many words:
 * `Permanent. No route puts a trip back.`
 *
 * `TripState.deleted` stays a `Register<boolean>` rather than a presence
 * flag, so a later slice that wants a restore adds an op type and writes
 * `false` without touching the fold's shape. Nothing here anticipates that,
 * and no surface may hint at it.
 */
export function tripDeleted(id: string): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: id,
    type: 'trip.deleted',
    payload: {},
  }
}

/**
 * `sync-protocol.md` §4.4: sets the per-person register to **present**
 * (§3.4), the same shape {@link gearTagApplied} has on tags.
 */
export function tripParticipantAdded(id: string, personId: string): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: id,
    type: 'trip.participant_added',
    payload: { person_id: personId },
  }
}

/**
 * `sync-protocol.md` §4.4: sets the per-person register to **absent** (§3.4).
 *
 * Not a delete — one register, written `false`, carrying a clock like any
 * other write, so a concurrent re-add wins on merit rather than on which
 * device happened to sync first.
 */
export function tripParticipantRemoved(id: string, personId: string): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: id,
    type: 'trip.participant_removed',
    payload: { person_id: personId },
  }
}

/**
 * `sync-protocol.md` §4.4: creates the Entry. A depot Entry **references**
 * gear by identity and copies nothing (invariant 8); a trip-only Entry
 * carries its own name and containment trait.
 *
 * The payload's `gear_id` is the wire's name for the `gearId` the state
 * holds, the same split `gear.owned_count_set{count}` has over `ownedCount`.
 */
export function tripEntryAdded(
  tripId: string,
  entryId: string,
  source: EntrySource,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.entry_added',
    payload: {
      entry_id: entryId,
      source:
        source.from === 'depot'
          ? { from: 'depot', gear_id: source.gearId }
          : {
              from: 'trip_only',
              name: source.name,
              container: source.container,
            },
    },
  }
}

/** §4.4: the tombstone. Also how an over-claim is settled (§3.6). */
export function tripEntryRemoved(tripId: string, entryId: string): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.entry_removed',
    payload: { entry_id: entryId },
  }
}

/**
 * §4.4: sets `bringCount`, absolutely. Counted Entries only — an authoring
 * rule this builder's callers honour and the reducer does not enforce.
 */
export function tripEntryBringCountSet(
  tripId: string,
  entryId: string,
  count: number,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.entry_bring_count_set',
    payload: { entry_id: entryId, count },
  }
}

/**
 * §4.4: tombstones one Participant's Piece. *This is* "that Person isn't
 * bringing one" (invariant 10) — there is no "not coming" status anywhere
 * (invariant 11).
 */
export function tripPieceRemoved(
  tripId: string,
  entryId: string,
  personId: string,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.piece_removed',
    payload: { entry_id: entryId, person_id: personId },
  }
}

/** §4.4: clears {@link tripPieceRemoved}'s tombstone, if strictly later. */
export function tripPieceRestored(
  tripId: string,
  entryId: string,
  personId: string,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.piece_restored',
    payload: { entry_id: entryId, person_id: personId },
  }
}

/** camelCase in, `snake_case` out — `authoring.ts` is the strict half. */
function wireResidence(residence: TripResidence): Record<string, unknown> {
  return residence.in === 'loose'
    ? { in: 'loose' }
    : { in: 'container', entry_id: residence.entryId }
}

/** §4.4. `status` is an open enum; the builder does not narrow it. */
export function tripEntryStatusSet(
  tripId: string,
  entryId: string,
  status: StatusValue,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.entry_status_set',
    payload: { entry_id: entryId, status },
  }
}

/** §4.4: {@link tripEntryStatusSet} for one Participant's Piece. */
export function tripPieceStatusSet(
  tripId: string,
  entryId: string,
  personId: string,
  status: StatusValue,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.piece_status_set',
    payload: { entry_id: entryId, person_id: personId, status },
  }
}

/**
 * §4.4: the Entry's **trip** residence. The payload is `snake_case` on the
 * wire, so `entryId` is written back out as `entry_id` — the one place this
 * slice performs its camel/snake split on the way out.
 */
export function tripEntryMoved(
  tripId: string,
  entryId: string,
  residence: TripResidence,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.entry_moved',
    payload: { entry_id: entryId, residence: wireResidence(residence) },
  }
}

/** §4.4: {@link tripEntryMoved} for one Participant's Piece. */
export function tripPieceMoved(
  tripId: string,
  entryId: string,
  personId: string,
  residence: TripResidence,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.piece_moved',
    payload: {
      entry_id: entryId,
      person_id: personId,
      residence: wireResidence(residence),
    },
  }
}

/** §4.4. One op moves the container and everything inside it (story 10). */
export function tripContainerStageSet(
  tripId: string,
  entryId: string,
  stage: StageValue,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.container_stage_set',
    payload: { entry_id: entryId, stage },
  }
}

/**
 * S10 (§4.4): the unpack outcome, on the Entry or — when `personId` is
 * given — on one Piece. `outcome: null` clears it back to open
 * (`reduce.ts`'s `writeNullableIfPresent`).
 *
 * `personId` is last and optional, and the key is omitted entirely when it
 * is absent — never `person_id: undefined`, which `exactOptionalPropertyTypes`
 * will not let this file write in the first place, but which JSON would also
 * silently drop, making the two builder calls indistinguishable on the wire.
 */
export function tripOutcomeSet(
  tripId: string,
  entryId: string,
  outcome: OutcomeValue | null,
  personId?: string,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.outcome_set',
    payload: {
      entry_id: entryId,
      outcome,
      ...(personId === undefined ? {} : { person_id: personId }),
    },
  }
}

/**
 * S10 (§4.4): sets `consumedCount` on the Entry, absolutely. Folds on any
 * Entry — the reducer does not gate on Kind, `consumedCountOf` does
 * (§1.4).
 */
export function tripConsumedCountSet(
  tripId: string,
  entryId: string,
  count: number,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.consumed_count_set',
    payload: { entry_id: entryId, count },
  }
}

/**
 * S11 (spec §2.1, §4): records that this Trip has posted `units` of `gear_id`
 * against the Depot's owned count — the running total, never a delta
 * (`gear.owned_count_set`'s own contract, restated one register over). The
 * close emits this once its reduction lands (spec §2.3); the offer emits it
 * after restoring a count a reopen had deducted (spec §3). Folds
 * unconditionally, on any Gear id and any Trip phase — `postedOf` is the
 * gate, on the way out.
 */
export function tripConsumptionPosted(
  tripId: string,
  gearId: string,
  units: number,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.consumption_posted',
    payload: { gear_id: gearId, units },
  }
}

/**
 * S13 (§4.4): adds a Pre-trip task, unticked. `text` is written
 * unconditionally by the reducer, which makes it this op's
 * `trip.created`-shaped seed — see `writeTask`.
 *
 * **`ticked` is not a payload field.** A new Task is unticked *by absence*
 * (`taskTickedOf`), so this op cannot contest the register, and a
 * `trip.task_ticked` that arrived first survives the add landing after it.
 *
 * The caller trims: a whitespace-only well commits nothing at all (spec §4),
 * so no op reaches here carrying a blank line.
 */
export function tripTaskAdded(
  tripId: string,
  taskId: string,
  text: string,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.task_added',
    payload: { task_id: taskId, text },
  }
}

/**
 * S13 (§4.4): ticks or unticks a Pre-trip task — **one op for both
 * directions**, because `true` and `false` are two values of one register
 * rather than a create and a delete.
 *
 * No needless-write guard is needed at the call site here, unlike
 * `PhaseSheet`'s or `JourneyRail`'s (`patterns.md` §2.3): the row's only
 * gesture flips the value, so an op equal to the current one is not
 * reachable.
 */
export function tripTaskTicked(
  tripId: string,
  taskId: string,
  ticked: boolean,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.task_ticked',
    payload: { task_id: taskId, ticked },
  }
}

/**
 * §4.4 (S12): posts a Trip note — free text, optionally *about* one Entry.
 *
 * **`entry_id` is omitted, never sent as `null`, for a Note about the Trip.**
 * This module is the strict half of the reader/writer pair (see the header):
 * a `null` on the wire means *clear this register*, and `NoteState.entryId`
 * is not nullable, so a `null` here would be authoring an instruction no
 * reader in this codebase honours — and inviting a future one to honour it.
 * An absent key is the honest way to say *about the Trip*.
 *
 * There is no rename and no delete for a Note, deliberately (ruling I12):
 * the catalogue defines two note ops and this is both of the writes.
 */
export function tripNotePosted(
  tripId: string,
  noteId: string,
  text: string,
  entryId?: string,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.note_posted',
    payload: {
      note_id: noteId,
      text,
      ...(entryId !== undefined && { entry_id: entryId }),
    },
  }
}

/**
 * §4.4 (S12): `true` keeps the Note as reference, `false` discards it.
 *
 * One op in both directions, exactly as `trip.task_ticked` and
 * `trip.entry_status_set` are — so a discard is undone by an ordinary later
 * keep rather than by a restore op (ruling I14). What "kept" then means
 * downstream is S14's: a Note **not discarded** is copied by the template,
 * which is kept *and* unreviewed both (I13).
 */
export function tripNoteKept(
  tripId: string,
  noteId: string,
  kept: boolean,
): OpSpec {
  return {
    aggregate: 'trip',
    aggregate_id: tripId,
    type: 'trip.note_kept',
    payload: { note_id: noteId, kept },
  }
}
